/**
 * @author JuhaK
 */
import { ILayerWrapperOptions, LayerWrapper } from '../LayerWrapper';
import { GtPointFeatureFactory } from './GtPointFeatureFactory';
import { makeObservable, observable } from 'mobx';
import { Notify } from '@shared/ui-native/Notify';
import WebGLPointsLayer from 'ol/layer/WebGLPoints';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import WebGLVectorLayerRenderer from 'ol/renderer/webgl/VectorLayer';
import Layer from 'ol/layer/Layer';
import Feature from 'ol/Feature';
import { GeometryCollection, LineString, Point as OlPoint } from 'ol/geom';
import { latLonToEN, removeFeaturesBySource } from '@openlayers/ol-utils';
import * as olStyle from 'ol/style';


class DeviationsWebGLLayer extends Layer {
    createRenderer() {
        return new WebGLVectorLayerRenderer(this, {
            style: {
                "shape-points": 3,
                "shape-rotation": ['get', 'rotation', 'number'],
                "shape-fill-color": 'black',
                "shape-rotate-with-view": true,
                'stroke-color': [0, 0, 0],
                'stroke-width': 1,
                'shape-radius': [
                    'interpolate',
                    ['exponential', 2],
                    ['zoom'],
                    9, 0,
                    15, 4,
                ]
            }
        });
    }
}

export class GtPointsLayer extends LayerWrapper<IOutlinePoint> {
    featureType: FeatureType = 'model_point';

    @observable public labelProps: string[] = [];
    @observable showLinePoints: boolean = false;
    @observable showDeviations: boolean = true;

    // we draw all point on webgl layer, and since it doesn't handle text, we draw labels on separate layer when zoomed in
    labelsLayer: VectorLayer<any> = null;

    deviationsLayer: DeviationsWebGLLayer = new DeviationsWebGLLayer({
        source: new VectorSource()
    });
    deviationsLabelsLayer: VectorLayer<any> = new VectorLayer({
        source: new VectorSource(),
        minZoom: 19
    });
    deviationsLayerAdded = false;

    constructor(opts: ILayerWrapperOptions<IOutlinePoint>) {
        opts.layerBuilderFn = () => new WebGLPointsLayer({
            style: {
                filter: ['!=', ['get', 'hidden', 'string'], 'true'],
                "circle-stroke-width": [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    17, 0,
                    17, 7,
                ],
                "circle-stroke-color": 'rgba(255,255,255,0.05)',
                "circle-radius": [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    17, 1,
                    17, 5,
                ],
                "circle-fill-color": [
                    "match", ["get", "hover"], 1,
                    "#0ca7ff",
                    "#000000"
                ],
                "circle-opacity": 1,
                'circle-scale': [
                    'interpolate',
                    ['exponential', 2],
                    ['zoom'],
                    5, 0.3,
                    15, 1,
                ]
            }
        })

        super(opts);
        makeObservable(this);

        const deviationsSource = (this.deviationsLayer.getSource() as VectorSource);
        const addOrRemoveDeviationsLayer = _.debounce(() => {
            if (!this.showDeviations || deviationsSource.isEmpty()) {
                this.removeDeviationsLayer();
            } else {
                this.addDeviationsLayer();
            }
        }, 10);
        deviationsSource.on('addfeature', addOrRemoveDeviationsLayer);
        deviationsSource.on('removefeature', addOrRemoveDeviationsLayer);
    }

    drawAll(items: IOutlinePoint[], structures?: IStructures[]): { features: Feature[]; failedToCreate: IOutlinePoint[] } {
        items.forEach(i => {
            const devA = i.props?.differenceA || i.props?.D_NORTH;
            const devB = i.props?.differenceB || i.props?.D_EAST;
            const devXY = i.props?.differenceXY || i.props?.DIFF_ERROR;

            if (devA && devB) {
                const source: FeatureSource = {
                    modelId: i.sourceModelId
                };

                const start = latLonToEN(i.lat, i.lon);
                const displaced = [start[0] + parseFloat(devB), start[1] + parseFloat(devA)];
                const end = [start[0] + 1, start[1]];
                const angle = Math.atan2(displaced[1] - start[1], displaced[0] - start[0]);

                const geometry = new GeometryCollection([
                    new LineString([start, end]),
                    new OlPoint(end)
                ])
                geometry.rotate(angle, start)

                const lineF = new Feature({
                    geometry,
                    rotation: -angle + 1.57,
                    source
                });

                const labelPoint = new OlPoint([start[0] + 2, start[1]]);
                labelPoint.rotate(angle, start)
                const labelF = new Feature({
                    geometry: labelPoint,
                    source
                });
                labelF.setStyle(new olStyle.Style({
                    text: new olStyle.Text({
                        text: parseFloat(devXY).toFixed(3),
                        overflow: true,
                        font: '13px monospace',
                        offsetY: -2,
                        textAlign: 'center',
                        textBaseline: 'middle',
                        fill: new olStyle.Fill({
                            color: 'black',
                        })
                    }),
                }));

                (this.deviationsLayer.getSource() as VectorSource).addFeature(lineF);
                (this.deviationsLabelsLayer.getSource() as VectorSource).addFeature(labelF);
            }
        });
        return super.drawAll(items, structures);
    }

    removeFeaturesBySource(source: FeatureSource, silent: boolean = true) {
        removeFeaturesBySource(this.deviationsLayer.getSource() as VectorSource, source, false);
        removeFeaturesBySource(this.deviationsLabelsLayer.getSource() as VectorSource, source, false);
        super.removeFeaturesBySource(source, silent);
    }

    onAddedToMap() {
        if (!this.labelsLayer) {
            this.labelsLayer = new VectorLayer({
                source: this.layerSource,
                updateWhileAnimating: false,
                updateWhileInteracting: false,
                minZoom: 19
            });

            this.labelsLayer.setStyle(feature => {
                const isLinePoint = feature.get('data').isLinePoint;
                const showPoints = !isLinePoint || (isLinePoint && this.showLinePoints);
                return GtPointFeatureFactory.styleFunction(feature, showPoints ? this.labelProps : []);
            });
        }

        this.map.addLayer(this.labelsLayer);

        if (this.showDeviations) {
            this.addDeviationsLayer();
        }
    }

    onRemovedFromMap() {
        this.removeDeviationsLayer();
        this.map.removeLayer(this.labelsLayer);
    }

    addDeviationsLayer = () => {
        if ((this.deviationsLayer.getSource() as VectorSource).isEmpty()) {
            return;
        }
        if (this.deviationsLayerAdded) return;
        this.deviationsLayerAdded = true;

        this.map.addLayer(this.deviationsLayer);
        this.deviationsLayer.setZIndex(this.layer.getZIndex() - 1);
        this.map.addLayer(this.deviationsLabelsLayer);
        this.deviationsLabelsLayer.setZIndex(this.layer.getZIndex() + 1);
    }

    removeDeviationsLayer = () => {
        if (!this.deviationsLayerAdded) return;
        this.deviationsLayerAdded = false;
        this.map.removeLayer(this.deviationsLayer);
        this.map.removeLayer(this.deviationsLabelsLayer);
    }

    setLabelProps(props: string | string[]) {
        this.labelProps = Array.isArray(props) ? props : [props];
        if (this.labelProps.length === 0 && this.labelsLayer) {
            this.map.removeLayer(this.labelsLayer);
            this.labelsLayer.dispose();
            this.labelsLayer = null;
        } else if (!this.labelsLayer) {
            this.onAddedToMap();
        }
        this.layerSource.dispatchEvent('change');
    }

    toggleLabelProp(prop: string) {
        const props = new Set(this.labelProps);
        if (props.has(prop)) {
            props.delete(prop);
        } else {
            if (this.labelProps.length >= 3) {
                Notify.warn('Up to 3 labels are allowed');
                return;
            }
            props.add(prop);
        }
        this.labelProps = Array.from(props);
        this.layerSource.dispatchEvent('change');
    }

    toggleShowPoints = () => {
        this.setShowPoints(!this.showLinePoints);
    }

    setShowPoints = (show: boolean) => {
        this.showLinePoints = show;
        this.layerSource.dispatchEvent('change');
    }

    toggleDeviations = () => {
        this.setShowDeviations(!this.showDeviations);
    }

    setShowDeviations = (show: boolean) => {
        this.showDeviations = show;

        if (this.showDeviations) {
            this.addDeviationsLayer();
        } else {
            this.removeDeviationsLayer();
        }

        setTimeout(() => {
            this.layerSource.dispatchEvent('change');
            this.map.getView().adjustZoom(0.0001);
        });
    }
}
